import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon, SunIcon, LockIcon, UnlockIcon, SettingsIcon, XIcon, SparklesIcon, RotateCcwIcon, SmartphoneIcon, ChevronRightIcon, InfoIcon } from 'lucide-react';
type PlayerState = 'splash' | 'onboarding' | 'playing' | 'settings';
export function VideoPlayer() {
  // Player state
  const [playerState, setPlayerState] = useState<PlayerState>('splash');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration] = useState(180); // 3 minutes
  // Prank states
  const [brightness, setBrightness] = useState(75);
  const [volume, setVolume] = useState(65);
  const [showControls, setShowControls] = useState(true);
  const [pauseButtonPosition, setPauseButtonPosition] = useState({
    x: 0,
    y: 0
  });
  const [showPremiumAd, setShowPremiumAd] = useState(false);
  const [adClosePosition, setAdClosePosition] = useState({
    x: 0,
    y: 0
  });
  const [showLockToast, setShowLockToast] = useState(false);
  const [showUnlockFlash, setShowUnlockFlash] = useState(false);
  const [brightnessGlow, setBrightnessGlow] = useState(false);
  const [volumeGlow, setVolumeGlow] = useState(false);
  const [prankEnabled, setPrankEnabled] = useState(true);
  const [showSeekConfirm, setShowSeekConfirm] = useState(false);
  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  // Passive randomization effect
  useEffect(() => {
    if (!isPlaying || !prankEnabled || isLocked) return;
    const randomize = () => {
      const delay = Math.random() * 3000 + 2000; // 2-5 seconds
      setTimeout(() => {
        if (!isPlaying || !prankEnabled) return;
        // Randomly change brightness or volume
        if (Math.random() > 0.5) {
          const newBrightness = Math.max(20, Math.min(100, brightness + (Math.random() - 0.5) * 40));
          setBrightness(Math.round(newBrightness));
          setBrightnessGlow(true);
          setTimeout(() => setBrightnessGlow(false), 400);
        } else {
          const newVolume = Math.max(10, Math.min(100, volume + (Math.random() - 0.5) * 50));
          setVolume(Math.round(newVolume));
          setVolumeGlow(true);
          setTimeout(() => setVolumeGlow(false), 400);
        }
        randomize();
      }, delay);
    };
    randomize();
  }, [isPlaying, prankEnabled, isLocked]);
  // Progress simulation
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setCurrentTime(t => Math.min(t + 1, duration));
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlaying, duration]);
  // Show premium ad occasionally
  useEffect(() => {
    if (!isPlaying || !prankEnabled) return;
    const timeout = setTimeout(() => {
      if (Math.random() > 0.5) {
        setShowPremiumAd(true);
      }
    }, 15000);
    return () => clearTimeout(timeout);
  }, [isPlaying, prankEnabled]);
  // Slippery pause button
  const handlePauseClick = useCallback((e: React.MouseEvent) => {
    if (prankEnabled && Math.random() > 0.5) {
      // Slip away!
      const newX = (Math.random() - 0.5) * 100;
      const newY = (Math.random() - 0.5) * 60;
      setPauseButtonPosition({
        x: newX,
        y: newY
      });
    } else {
      setIsPlaying(!isPlaying);
      setPauseButtonPosition({
        x: 0,
        y: 0
      });
    }
  }, [isPlaying, prankEnabled]);
  // Lock/unlock with randomization trap
  const handleLockToggle = () => {
    if (isLocked) {
      // Unlock - randomize everything!
      if (prankEnabled) {
        setBrightness(Math.round(Math.random() * 80 + 10));
        setVolume(Math.round(Math.random() * 90 + 10));
        setShowUnlockFlash(true);
        setTimeout(() => setShowUnlockFlash(false), 300);
      }
    } else {
      setShowLockToast(true);
      setTimeout(() => setShowLockToast(false), 2000);
    }
    setIsLocked(!isLocked);
  };
  // Slippery ad close button
  const handleAdClose = () => {
    if (prankEnabled && Math.random() > 0.4) {
      const newX = (Math.random() - 0.5) * 40;
      const newY = (Math.random() - 0.5) * 40;
      setAdClosePosition({
        x: newX,
        y: newY
      });
    } else {
      setShowPremiumAd(false);
      setAdClosePosition({
        x: 0,
        y: 0
      });
    }
  };
  // Seek with fake confirmation
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (prankEnabled) {
      setShowSeekConfirm(true);
      setTimeout(() => setShowSeekConfirm(false), 2000);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      setCurrentTime(Math.round(percent * duration));
    }
  };
  // Splash Screen
  if (playerState === 'splash') {
    return <div className="w-full h-full bg-[#0A0A0B] flex flex-col items-center justify-center relative overflow-hidden" data-id="element-168">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" data-id="element-169" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="text-center space-y-6 z-10" data-id="element-170">
          {/* Logo */}
          <motion.div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20" animate={{
          boxShadow: ['0 10px 40px rgba(245, 158, 11, 0.2)', '0 10px 60px rgba(245, 158, 11, 0.4)', '0 10px 40px rgba(245, 158, 11, 0.2)']
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-171">
            <PlayIcon className="w-10 h-10 text-black" fill="currentColor" data-id="element-172" />
          </motion.div>

          <div className="space-y-2" data-id="element-173">
            <h1 className="text-3xl font-bold text-white" data-id="element-174">Chaos Player</h1>
            <p className="text-zinc-500 text-sm" data-id="element-175">
              Optimize your viewing experience
            </p>
          </div>

          <motion.button onClick={() => setPlayerState('onboarding')} className="px-8 py-3 rounded-xl bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-176">
            Get Started
          </motion.button>

          <p className="text-xs text-zinc-600" data-id="element-177">Version 2.4.1</p>
        </motion.div>
      </div>;
  }
  // Onboarding Screen
  if (playerState === 'onboarding') {
    return <div className="w-full h-full bg-[#0A0A0B] flex flex-col items-center justify-center p-6 relative" data-id="element-178">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="max-w-sm w-full space-y-8" data-id="element-179">
          <div className="text-center space-y-2" data-id="element-180">
            <h2 className="text-2xl font-bold text-white" data-id="element-181">Tilt Controls</h2>
            <p className="text-zinc-400 text-sm" data-id="element-182">
              Use device motion to control playback
            </p>
          </div>

          {/* Animated phone demo */}
          <div className="flex justify-center py-8" data-id="element-183">
            <motion.div className="relative" animate={{
            rotateZ: [-15, 15, -15],
            rotateX: [-10, 10, -10]
          }} transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-184">
              <div className="w-16 h-28 rounded-xl border-2 border-amber-500/60 bg-zinc-900 relative" data-id="element-185">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-4 h-1 rounded-full bg-zinc-700" data-id="element-186" />
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full border border-zinc-700" data-id="element-187" />
              </div>
            </motion.div>
          </div>

          {/* Control mappings */}
          <div className="space-y-3" data-id="element-188">
            <div className="flex items-center gap-4 p-3 rounded-xl bg-zinc-900/50 border border-white/5" data-id="element-189">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center" data-id="element-190">
                <SunIcon className="w-5 h-5 text-amber-500" data-id="element-191" />
              </div>
              <div data-id="element-192">
                <p className="text-sm font-medium text-white" data-id="element-193">
                  Tilt Left/Right
                </p>
                <p className="text-xs text-zinc-500" data-id="element-194">Adjust brightness</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-3 rounded-xl bg-zinc-900/50 border border-white/5" data-id="element-195">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center" data-id="element-196">
                <Volume2Icon className="w-5 h-5 text-amber-500" data-id="element-197" />
              </div>
              <div data-id="element-198">
                <p className="text-sm font-medium text-white" data-id="element-199">
                  Tilt Forward/Back
                </p>
                <p className="text-xs text-zinc-500" data-id="element-200">Adjust volume</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-3 rounded-xl bg-zinc-900/50 border border-white/5" data-id="element-201">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center" data-id="element-202">
                <RotateCcwIcon className="w-5 h-5 text-amber-500" data-id="element-203" />
              </div>
              <div data-id="element-204">
                <p className="text-sm font-medium text-white" data-id="element-205">Rotate Device</p>
                <p className="text-xs text-zinc-500" data-id="element-206">Scrub timeline</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3" data-id="element-207">
            <button onClick={() => setPlayerState('playing')} className="flex-1 py-3 rounded-xl bg-zinc-800 text-zinc-300 font-medium hover:bg-zinc-700 transition-colors" data-id="element-208">
              Skip
            </button>
            <button onClick={() => setPlayerState('playing')} className="flex-1 py-3 rounded-xl bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors" data-id="element-209">
              Continue
            </button>
          </div>
        </motion.div>
      </div>;
  }
  // Settings Screen
  if (playerState === 'settings') {
    return <div className="w-full h-full bg-[#0A0A0B] flex flex-col" data-id="element-210">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b border-white/5" data-id="element-211">
          <button onClick={() => setPlayerState('playing')} className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center hover:bg-zinc-800 transition-colors" data-id="element-212">
            <ChevronRightIcon className="w-5 h-5 text-zinc-400 rotate-180" data-id="element-213" />
          </button>
          <h2 className="text-xl font-semibold text-white" data-id="element-214">Settings</h2>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-6" data-id="element-215">
          {/* Playback Settings */}
          <div className="space-y-3" data-id="element-216">
            <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider" data-id="element-217">
              Playback
            </h3>
            <div className="space-y-2" data-id="element-218">
              <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/50 border border-white/5" data-id="element-219">
                <div data-id="element-220">
                  <p className="font-medium text-white" data-id="element-221">Stability Mode</p>
                  <p className="text-xs text-zinc-500" data-id="element-222">
                    Enable adaptive optimization
                  </p>
                </div>
                <button onClick={() => setPrankEnabled(!prankEnabled)} className={`w-12 h-7 rounded-full transition-colors ${prankEnabled ? 'bg-amber-500' : 'bg-zinc-700'}`} data-id="element-223">
                  <motion.div className="w-5 h-5 rounded-full bg-white shadow-md" animate={{
                  x: prankEnabled ? 22 : 4
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-224" />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/50 border border-white/5" data-id="element-225">
                <div data-id="element-226">
                  <p className="font-medium text-white" data-id="element-227">Tilt Controls</p>
                  <p className="text-xs text-zinc-500" data-id="element-228">Use device motion</p>
                </div>
                <button className="w-12 h-7 rounded-full bg-amber-500" data-id="element-229">
                  <motion.div className="w-5 h-5 rounded-full bg-white shadow-md" animate={{
                  x: 22
                }} data-id="element-230" />
                </button>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/50 border border-white/5" data-id="element-231">
                <div data-id="element-232">
                  <p className="font-medium text-white" data-id="element-233">Show Hints</p>
                  <p className="text-xs text-zinc-500" data-id="element-234">Display control tips</p>
                </div>
                <button className="w-12 h-7 rounded-full bg-amber-500" data-id="element-235">
                  <motion.div className="w-5 h-5 rounded-full bg-white shadow-md" animate={{
                  x: 22
                }} data-id="element-236" />
                </button>
              </div>
            </div>
          </div>

          {/* Accessibility */}
          <div className="space-y-3" data-id="element-237">
            <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider" data-id="element-238">
              Accessibility
            </h3>
            <div className="p-4 rounded-xl bg-zinc-900/50 border border-white/5 space-y-3" data-id="element-239">
              <div className="flex items-center justify-between" data-id="element-240">
                <div data-id="element-241">
                  <p className="font-medium text-white" data-id="element-242">Safe Mode</p>
                  <p className="text-xs text-zinc-500" data-id="element-243">
                    Disable all adaptive features
                  </p>
                </div>
                <button onClick={() => setPrankEnabled(false)} className={`w-12 h-7 rounded-full transition-colors ${!prankEnabled ? 'bg-amber-500' : 'bg-zinc-700'}`} data-id="element-244">
                  <motion.div className="w-5 h-5 rounded-full bg-white shadow-md" animate={{
                  x: !prankEnabled ? 22 : 4
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} data-id="element-245" />
                </button>
              </div>
              <p className="text-xs text-zinc-600" data-id="element-246">
                Enable this option if you experience motion sensitivity or need
                standard controls.
              </p>
            </div>
          </div>

          {/* About */}
          <div className="space-y-3" data-id="element-247">
            <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider" data-id="element-248">
              About
            </h3>
            <div className="p-4 rounded-xl bg-zinc-900/50 border border-white/5 space-y-2" data-id="element-249">
              <div className="flex justify-between" data-id="element-250">
                <span className="text-zinc-400" data-id="element-251">Version</span>
                <span className="text-white" data-id="element-252">2.4.1</span>
              </div>
              <div className="flex justify-between" data-id="element-253">
                <span className="text-zinc-400" data-id="element-254">Build</span>
                <span className="text-white" data-id="element-255">2024.01.15</span>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Main Player
  return <div className="w-full h-full bg-black relative overflow-hidden select-none" style={{
    filter: `brightness(${brightness / 100})`
  }} onClick={() => setShowControls(!showControls)} data-id="element-256">
      {/* Video placeholder */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black flex items-center justify-center" data-id="element-257">
        <div className="text-center space-y-4 opacity-30" data-id="element-258">
          <PlayIcon className="w-16 h-16 mx-auto text-zinc-600" data-id="element-259" />
          <p className="text-zinc-600 text-sm" data-id="element-260">Video Content</p>
        </div>
      </div>

      {/* Unlock flash overlay */}
      <AnimatePresence data-id="element-261">
        {showUnlockFlash && <motion.div className="absolute inset-0 bg-amber-500/30 z-50" initial={{
        opacity: 1
      }} animate={{
        opacity: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-262" />}
      </AnimatePresence>

      {/* Controls overlay */}
      <AnimatePresence data-id="element-263">
        {showControls && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 z-10" onClick={e => e.stopPropagation()} data-id="element-264">
            {/* Top bar */}
            <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between" data-id="element-265">
              <div className="flex items-center gap-3" data-id="element-266">
                <h3 className="text-white font-medium" data-id="element-267">Sample Video</h3>
                {isLocked && <span className="px-2 py-1 rounded-md bg-zinc-800/80 text-xs text-zinc-400 flex items-center gap-1" data-id="element-268">
                    <LockIcon className="w-3 h-3" data-id="element-269" />
                    Locked
                  </span>}
              </div>
              <button onClick={() => setPlayerState('settings')} className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center hover:bg-white/20 transition-colors" data-id="element-270">
                <SettingsIcon className="w-5 h-5 text-white" data-id="element-271" />
              </button>
            </div>

            {/* Center play/pause */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-272">
              <motion.button onClick={handlePauseClick} className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-colors" animate={{
            x: pauseButtonPosition.x,
            y: pauseButtonPosition.y
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 25
          }} data-id="element-273">
                {isPlaying ? <PauseIcon className="w-8 h-8 text-white" fill="currentColor" data-id="element-274" /> : <PlayIcon className="w-8 h-8 text-white" fill="currentColor" data-id="element-275" />}
              </motion.button>
            </div>

            {/* Bottom controls */}
            <div className="absolute bottom-0 left-0 right-0 p-4 space-y-4" data-id="element-276">
              {/* Progress bar */}
              <div className="h-1 bg-white/20 rounded-full cursor-pointer relative group" onClick={handleSeek} data-id="element-277">
                <div className="h-full bg-amber-500 rounded-full relative" style={{
              width: `${currentTime / duration * 100}%`
            }} data-id="element-278">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-amber-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-279" />
                </div>
              </div>

              {/* Controls row */}
              <div className="flex items-center justify-between" data-id="element-280">
                <div className="flex items-center gap-4" data-id="element-281">
                  {/* Time */}
                  <span className="text-sm text-white font-mono" data-id="element-282">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                <div className="flex items-center gap-3" data-id="element-283">
                  {/* Brightness indicator */}
                  <motion.div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10" animate={brightnessGlow ? {
                boxShadow: ['0 0 0px rgba(245, 158, 11, 0)', '0 0 15px rgba(245, 158, 11, 0.6)', '0 0 0px rgba(245, 158, 11, 0)']
              } : {}} transition={{
                duration: 0.4
              }} data-id="element-284">
                    <SunIcon className="w-4 h-4 text-amber-500" data-id="element-285" />
                    <motion.span key={brightness} className="text-xs text-white font-mono w-6" initial={{
                  scale: 1
                }} animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 0.15
                }} data-id="element-286">
                      {brightness}
                    </motion.span>
                  </motion.div>

                  {/* Volume indicator */}
                  <motion.div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10" animate={volumeGlow ? {
                boxShadow: ['0 0 0px rgba(245, 158, 11, 0)', '0 0 15px rgba(245, 158, 11, 0.6)', '0 0 0px rgba(245, 158, 11, 0)']
              } : {}} transition={{
                duration: 0.4
              }} data-id="element-287">
                    {volume > 0 ? <Volume2Icon className="w-4 h-4 text-amber-500" data-id="element-288" /> : <VolumeXIcon className="w-4 h-4 text-amber-500" data-id="element-289" />}
                    <motion.span key={volume} className="text-xs text-white font-mono w-6" initial={{
                  scale: 1
                }} animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 0.15
                }} data-id="element-290">
                      {volume}
                    </motion.span>
                  </motion.div>

                  {/* Lock button */}
                  <button onClick={handleLockToggle} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-id="element-291">
                    {isLocked ? <LockIcon className="w-5 h-5 text-amber-500" data-id="element-292" /> : <UnlockIcon className="w-5 h-5 text-white" data-id="element-293" />}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lock toast */}
      <AnimatePresence data-id="element-294">
        {showLockToast && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="absolute bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl bg-zinc-900/90 backdrop-blur border border-white/10 flex items-center gap-2 z-20" data-id="element-295">
            <LockIcon className="w-4 h-4 text-amber-500" data-id="element-296" />
            <span className="text-sm text-white" data-id="element-297">Locked</span>
          </motion.div>}
      </AnimatePresence>

      {/* Seek confirmation dialog */}
      <AnimatePresence data-id="element-298">
        {showSeekConfirm && <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="absolute inset-0 flex items-center justify-center bg-black/60 z-30" data-id="element-299">
            <div className="bg-zinc-900 rounded-2xl p-6 max-w-xs w-full mx-4 border border-white/10 space-y-4" data-id="element-300">
              <div className="flex items-center gap-3" data-id="element-301">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center" data-id="element-302">
                  <InfoIcon className="w-5 h-5 text-amber-500" data-id="element-303" />
                </div>
                <div data-id="element-304">
                  <h4 className="font-semibold text-white" data-id="element-305">Calibrating</h4>
                  <p className="text-xs text-zinc-500" data-id="element-306">Please wait...</p>
                </div>
              </div>
              <p className="text-sm text-zinc-400" data-id="element-307">
                Optimizing playback position for your viewing experience.
              </p>
              <div className="h-1 bg-zinc-800 rounded-full overflow-hidden" data-id="element-308">
                <motion.div className="h-full bg-amber-500" initial={{
              width: '0%'
            }} animate={{
              width: '100%'
            }} transition={{
              duration: 2
            }} data-id="element-309" />
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Premium ad modal */}
      <AnimatePresence data-id="element-310">
        {showPremiumAd && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 flex items-center justify-center bg-black/80 z-40" data-id="element-311">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-3xl p-6 max-w-sm w-full mx-4 border border-white/10 relative" data-id="element-312">
              {/* Close button */}
              <motion.button onClick={handleAdClose} className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" animate={{
            x: adClosePosition.x,
            y: adClosePosition.y
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 25
          }} data-id="element-313">
                <XIcon className="w-4 h-4 text-white" data-id="element-314" />
              </motion.button>

              <div className="text-center space-y-4 pt-4" data-id="element-315">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center" data-id="element-316">
                  <SparklesIcon className="w-8 h-8 text-black" data-id="element-317" />
                </div>

                <div className="space-y-2" data-id="element-318">
                  <h3 className="text-xl font-bold text-white" data-id="element-319">
                    Upgrade to Premium
                  </h3>
                  <p className="text-sm text-zinc-400" data-id="element-320">
                    Unlock advanced stabilization and enhanced viewing features.
                  </p>
                </div>

                <div className="space-y-2 text-left" data-id="element-321">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5" data-id="element-322">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center" data-id="element-323">
                      <SunIcon className="w-4 h-4 text-amber-500" data-id="element-324" />
                    </div>
                    <span className="text-sm text-white" data-id="element-325">
                      Auto brightness optimization
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5" data-id="element-326">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center" data-id="element-327">
                      <Volume2Icon className="w-4 h-4 text-amber-500" data-id="element-328" />
                    </div>
                    <span className="text-sm text-white" data-id="element-329">
                      Intelligent volume control
                    </span>
                  </div>
                </div>

                <button className="w-full py-3 rounded-xl bg-amber-500 text-black font-semibold hover:bg-amber-400 transition-colors" data-id="element-330">
                  Try Premium Free
                </button>

                <p className="text-xs text-zinc-600" data-id="element-331">No credit card required</p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}